import { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles.css';

const EMPTY_SONG_FORM = {
  title: '',
  composer: '',
  google_drive_file_id: '',
  github_file_url: '',
  permalink: '',
  is_public: true,
  source: 'google_drive',
  audio_url: '',
  lyrics: '',
  description: '',
  youtube_url: '',
  category: '',
  tags: ''
};

    // Color-coded tier dropdown, allows quick admin tier toggle
    function TierDropdown({ user, setUsers }) {
      const [loading, setLoading] = useState(false);
      const TIER_OPTIONS = [
        { value: "choir_member", label: "Choir Member", color: "bg-gray-200 text-gray-900" },
        { value: "choir_supporter", label: "Supporter", color: "bg-green-700 text-white" },
        { value: "choir_expert", label: "Expert", color: "bg-yellow-500 text-white" },
      ];
      const current = TIER_OPTIONS.find(t => t.value === user.subscription_tier);
    
      const handleChange = async (e) => {
        setLoading(true);
        const newTier = e.target.value;
        try {
          const res = await fetch("/.netlify/functions/update-user-tier", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId: user.id, tier: newTier }),
          });
          if (res.ok) {
            setUsers(prev =>
              prev.map(u =>
                u.id === user.id ? { ...u, subscription_tier: newTier } : u
              )
            );
          }
        } catch (err) { }
        setLoading(false);
      };
    
      return (
        <select
          className={`rounded-full px-3 py-1 border border-gray-200 text-xs min-w-[110px] ${current?.color}`}
          value={user.subscription_tier || ""}
          onChange={handleChange}
          disabled={loading}
        >
          <option value="">-</option>
          {TIER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }
    
      // User Add/Edit Modal
          function UserModal({ open, mode, user, onClose, onSave, currentUser }) {
            const [form, setForm] = useState({
              full_name: user?.full_name || "",
              email: user?.email || "",
              phone_number: user?.phone_number || "",
              subscription_tier: user?.subscription_tier || "choir_member",
            });
            const [saving, setSaving] = useState(false);
            const [error, setError] = useState("");
          
            const handleSave = async () => {
              setSaving(true); setError("");
              try {
                if (!form.full_name || !form.email) {
                  setError("Name & Email are required.");
                  setSaving(false);
                  return;
                }
                // Call your backend (or Supabase) to add or update the user
                const endpoint = mode === "add"
                  ? "/.netlify/functions/add-user"
                  : "/.netlify/functions/update-user";
                const payload = mode === "add"
                  ? { ...form }
                  : { ...form, id: user.id };
                const res = await fetch(endpoint, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(payload),
                });
                if (!res.ok) {
                  setError("Error saving user.");
                  setSaving(false);
                  return;
                }
                await onSave(form);
              } catch {
                setError("Unexpected error.");
              }
              setSaving(false);
            };
          
            return (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                <div className="bg-white rounded-2xl shadow-xl max-w-[95vw] w-[400px] p-6 relative">
                  <h3 className="text-xl font-bold mb-3">{mode === "add" ? "Add User" : "Edit User"}</h3>
                  <div className="flex flex-col gap-3">
                    <input
                      type="text"
                      className="border rounded-lg px-3 py-2"
                      placeholder="Full Name"
                      value={form.full_name}
                      onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                    />
                    <input
                      type="email"
                      className="border rounded-lg px-3 py-2"
                      placeholder="Email"
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      disabled={mode === "edit"}
                    />
                    <input
                      type="text"
                      className="border rounded-lg px-3 py-2"
                      placeholder="Phone Number"
                      value={form.phone_number}
                      onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))}
                    />
                    <select
                      className="border rounded-lg px-3 py-2"
                      value={form.subscription_tier}
                      onChange={e => setForm(f => ({ ...f, subscription_tier: e.target.value }))}
                    >
                      <option value="choir_member">Choir Member</option>
                      <option value="choir_supporter">Supporter</option>
                      <option value="choir_expert">Expert</option>
                    </select>
                    {error && <div className="text-red-500 text-sm">{error}</div>}
                  </div>
                  <div className="flex justify-end mt-5 gap-2">
                    <button
                      className="bg-gray-100 hover:bg-gray-200 rounded-lg px-4 py-2"
                      onClick={onClose}
                      disabled={saving}
                    >Cancel</button>
                    <button
                      className="bg-blue-600 text-white rounded-lg px-4 py-2 hover:bg-blue-700 font-bold"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          
          // Confirm delete modal
          function ConfirmDeleteModal({ user, onClose, onConfirm }) {
            return (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                <div className="bg-white rounded-2xl shadow-xl p-6 max-w-[90vw] w-[340px] flex flex-col gap-5 items-center">
                  <div className="text-xl text-center font-semibold">Delete User?</div>
                  <div className="text-center text-gray-600">
                    Are you sure you want to permanently delete <b>{user.full_name || user.email}</b>?
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      className="bg-gray-200 rounded-lg px-4 py-2"
                      onClick={onClose}
                    >Cancel</button>
                    <button
                      className="bg-rose-600 text-white rounded-lg px-4 py-2 font-bold"
                      onClick={onConfirm}
                    >Yes, Delete</button>
                  </div>
                </div>
              </div>
            );
          }

function Admin() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [songs, setSongs] = useState([]);

  const [musicBooks, setMusicBooks] = useState([]);
  const [editingBookId, setEditingBookId] = useState(null);
  const [bookForm, setBookForm] = useState({
    title: '',
    author: '',
    google_drive_file_id: '',
    description: '',
    is_public: true,
    tags: '',
  });

  const [selectedBooks, setSelectedBooks] = useState([]);
  
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);

  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  
  // Handler to add new category
  const addSongCategory = (cat) => {
    if (!SONG_CATEGORIES.includes(cat)) {
      setSONG_CATEGORIES([...SONG_CATEGORIES, cat]);
      // Optionally: persist new category to your backend/db
    }
  };
  // Handler to update category for a song
  const updateSongCategory = async (songId, category) => {
  // Update in Supabase first
  const { error } = await supabase
    .from('songs')
    .update({ category })
    .eq('id', songId);

  if (error) {
    alert('Failed to update category: ' + error.message);
    return;
  }

  // Update in local state for instant UI feedback
  const updatedSongs = songs.map(song =>
    song.id === songId ? { ...song, category } : song
  );
  setSongs(updatedSongs);
};

  const syncScroll = (sourceRef, targetRef) => {
    if (sourceRef.current && targetRef.current) {
      targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
    }
  };
  const [posts, setPosts] = useState([]);
  // For Blog Posts search and pagination
  const [postSearch, setPostSearch] = useState('');
  const [postPage, setPostPage] = useState(0);
  const postsPerPage = 8;
  
  const filteredPosts = posts
    .filter(post => {
      const titleMatch = (post.title || '').toLowerCase().includes(postSearch.toLowerCase());
      const tagsMatch = Array.isArray(post.tags)
        ? post.tags.some(tag => tag.toLowerCase().includes(postSearch.toLowerCase()))
        : false;
      return titleMatch || tagsMatch;
    })
    .slice(postPage * postsPerPage, postPage * postsPerPage + postsPerPage);

  const [users, setUsers] = useState([]);
  const [totalDonatedUsers, setTotalDonatedUsers] = useState(0);
  const [ads, setAds] = useState([]);
  const [messages, setMessages] = useState([]);
  const [songForm, setSongForm] = useState(EMPTY_SONG_FORM);
  const [quillKey, setQuillKey] = useState(0);
  const [postForm, setPostForm] = useState({ 
    title: '', 
    content: '', 
    permalink: '', 
    meta_description: '', 
    tags: '', 
    category: '', 
    focus_keyword: '',
    featured_image_url: ''
  });
  const [adForm, setAdForm] = useState({
    name: '',
    code: '',
    position: 'home_above_sotw',
    is_active: true
  });
  const [analyticsData, setAnalyticsData] = useState({ ga: null, gsc: null });
  const [visitorData, setVisitorData] = useState([]);
  const [topSongs, setTopSongs] = useState([]);
  const [topPosts, setTopPosts] = useState([]);
  const [songOfTheWeekHtml, setSongOfTheWeekHtml] = useState('');
  const [songOfTheWeekTitle, setSongOfTheWeekTitle] = useState('');
  const [songOfTheWeekComposer, setSongOfTheWeekComposer] = useState('');
  const [editingSongId, setEditingSongId] = useState(null);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editingAdId, setEditingAdId] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [songSearch, setSongSearch] = useState('');
  const [songFilter, setSongFilter] = useState('all');
  const [songCategoryFilter, setSongCategoryFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [analyticsSection, setAnalyticsSection] = useState('local');
  const [visitorTimeFilter, setVisitorTimeFilter] = useState('all');
  const [visitorPage, setVisitorPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [userModal, setUserModal] = useState({ open: false, mode: null, user: null });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, user: null });
  const [userTierFilter, setUserTierFilter] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [visitorSearch, setVisitorSearch] = useState('');
  const [visitorStatusFilter, setVisitorStatusFilter] = useState('all');
  const [visitorTypeFilter, setVisitorTypeFilter] = useState('all');
  const navigate = useNavigate();

  const [searchTrends, setSearchTrends] = useState([]);

  const searchTrendsTimeout = useRef(null);

  const fetchSearchTrends = useCallback(async () => {
    setError(null); // Clear previous error
    try {
      const { data, error } = await supabase
        .rpc('get_search_trends')
        .limit(10);
      if (error) throw error;
      setSearchTrends(data || []);
    } catch (err) {
      setError('Failed to load search trends: ' + err.message);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'analytics') {
      // Debounce the fetch to prevent multiple calls
      if (searchTrendsTimeout.current) {
        clearTimeout(searchTrendsTimeout.current);
      }
      searchTrendsTimeout.current = setTimeout(() => {
        fetchSearchTrends();
      }, 300);
    }
    return () => {
      if (searchTrendsTimeout.current) {
        clearTimeout(searchTrendsTimeout.current);
      }
    };
  }, [activeTab, fetchSearchTrends]);

  const location = useLocation();

  // Initial fetch and auth check
  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        navigate('/login');
        setLoading(false);
        return;
      }
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData.user) {
        navigate('/login');
        setLoading(false);
        return;
      }
      setUser(userData.user);

      const { data: adminData, error: adminError } = await supabase
        .from('admins')
        .select('user_id')
        .eq('user_id', userData.user.id)
        .single();
      if (adminError || !adminData) {
        navigate('/');
        setLoading(false);
        return;
      }

      await Promise.all([
        fetchSongs(),
        fetchPosts(),
        fetchUsers(),
        fetchAds(),
        fetchMessages(),
        fetchAnalytics(),
        fetchVisitors(),
        fetchTopSongs(),
        fetchTopPosts(),
        fetchSongOfTheWeek(),
        fetchMusicBooks()
      ]);

      const visitorInterval = setInterval(fetchVisitors, 300000);
      setLoading(false);
      return () => clearInterval(visitorInterval);
    };
    fetchUser();
  }, [navigate]);

  // Handle URL changes for tab and song editing
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    const editSongId = urlParams.get('editSongId');

    if (tab) {
      setActiveTab(tab);
    }

    if (editSongId) {
      const songId = parseInt(editSongId, 10);
      if (!isNaN(songId)) {
        const loadSongForEdit = async () => {
          setLoading(true);
          try {
            const { data, error } = await supabase
              .from('songs')
              .select('*')
              .eq('id', songId)
              .single();
            if (error) throw error;
            if (data) {
              const newSongForm = {
                title: data.title || '',
                composer: data.composer || '',
                google_drive_file_id: data.google_drive_file_id || '',
                github_file_url: data.github_file_url || '',
                permalink: data.permalink || '',
                is_public: data.is_public !== false,
                source: data.google_drive_file_id ? 'google_drive' : 'github',
                audio_url: data.audio_url || '',
                lyrics: data.lyrics || '',
                description: data.description || '',
                youtube_url: data.youtube_url || '',
                category: data.category || '',
                tags: data.tags ? data.tags.join(', ') : ''
              };
              setSongForm(newSongForm);
              setEditingSongId(songId);
              setActiveTab('songs'); // Ensure songs tab is active
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          } catch (err) {
            setError('Failed to load song for editing: ' + err.message);
          }
          setLoading(false);
        };
        loadSongForEdit();
      }
    }
  }, [location]);  

  const [SONG_CATEGORIES, setSONG_CATEGORIES] = useState([
  'Entrance', 'Kyrie', 'Gloria', 'Responsorial Psalm',
  'Gospel Acclamation', 'Credo', 'Response to prayers', 'Preconsecration', 'Offertory', 'Sanctus',
  'Agnus Dei', 'Communion', 'Dismissal', 'Lent', 'Pentecost', 'Easter',
  'Advent', 'Christmas', 'Trinity', 'Marian', 'Classical'
  ]);

  const fetchSongs = async () => {
    setLoading(true);
    let allSongs = [];
    let offset = 0;
    const limit = 1000; // Batch size

    try {
      while (true) {
        const { data, error } = await supabase
          .from('songs')
          .select('*')
          .range(offset, offset + limit - 1); // Fetch in chunks
        if (error) throw error;
        if (!data || data.length === 0) break; // No more data to fetch
        allSongs = allSongs.concat(data);
        offset += limit;
        console.log(`Fetched ${data.length} songs, total so far: ${allSongs.length}`);
      }
      setSongs(allSongs);
      console.log('Total songs loaded:', allSongs.length);
    } catch (err) {
      setError('Failed to load songs: ' + err.message);
      console.error('Fetch songs error:', err);
    }
    setLoading(false);
  };

  const fetchPosts = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('blog_posts').select('*');
    if (error) setError('Failed to load posts: ' + error.message);
    else setPosts(data || []);
    setLoading(false);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch('/.netlify/functions/fetch-users');
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data || []);
      const donatedCount = data.filter(user => user.has_donated).length;
      setTotalDonatedUsers(donatedCount);
    } catch (err) {
      setError('Failed to load users: ' + err.message);
    }
    setLoading(false);
  };

  const fetchAds = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('advertisements').select('*');
    if (error) setError('Failed to load ads: ' + error.message);
    else setAds(data || []);
    setLoading(false);
  };

  const fetchMessages = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('contact_messages')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) setError('Failed to load messages: ' + error.message);
    else setMessages(data || []);
    setLoading(false);
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch('/.netlify/functions/analytics');
      if (!response.ok) throw new Error('Failed to fetch analytics data');
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      setAnalyticsData(data);
    } catch (err) {
      setError('Analytics fetch failed: ' + err.message);
    }
    setLoading(false);
  };

  const fetchVisitors = async () => {
  setLoading(true);
  try {
    let query = supabase
      .from('visitors')
      .select(`
        visit_timestamp,
        page_url,
        ip_address,
        user_agent,
        user_id,
        profiles:profiles!fk_visitors_user_id(id, email, subscription_tier)
      `)
      .order('visit_timestamp', { ascending: false });

    if (visitorTimeFilter !== 'all') {
      const now = new Date();
      let startDate;
      switch (visitorTimeFilter) {
        case '24h': startDate = new Date(now - 24 * 60 * 60 * 1000); break;
        case '7d': startDate = new Date(now - 7 * 24 * 60 * 60 * 1000); break;
        case '30d': startDate = new Date(now - 30 * 24 * 60 * 60 * 1000); break;
        default: break;
      }
      query = query.gte('visit_timestamp', startDate.toISOString());
    }

    const { data, error } = await query.range(visitorPage * 50, (visitorPage + 1) * 50 - 1);
    if (error) throw error;

    console.log('Raw visitor data:', data);

    const aggregated = {};
    (data || []).forEach(visit => {
      const key = `${visit.ip_address}-${visit.page_url}-${visit.user_id || 'guest'}`;
      if (!aggregated[key] || new Date(visit.visit_timestamp) > new Date(aggregated[key].visit_timestamp)) {
        const isBot = visit.user_agent && /bot|crawl|spider|googlebot|bingbot|yahoo|baiduspider|duckduckbot|slurp|ahrefsbot|semrushbot/i.test(visit.user_agent);
        const isOnline = new Date(visit.visit_timestamp).getTime() > Date.now() - 30 * 60 * 1000 - (1 * 60 * 60 * 1000); // Adjust for WAT (UTC+1)
        aggregated[key] = {
          ...visit,
          is_registered: !!visit.user_id,
          is_bot: isBot,
          is_online: isOnline,
          profiles: visit.profiles || { id: null, email: null, subscription_tier: null }
        };
      }
    });
    const uniqueVisits = Object.values(aggregated)
      .sort((a, b) => new Date(b.visit_timestamp) - new Date(a.visit_timestamp));

    console.log('Processed visitor data:', uniqueVisits);
    setVisitorData(uniqueVisits);
    setError(null);
  } catch (err) {
    const errorMessage = err.message || 'Unknown error occurred';
    setError(`Failed to load visitor data: ${errorMessage}`);
    console.error('Fetch visitors error:', err);
    setVisitorData([]);
  }
  setLoading(false);
};

  const fetchTopSongs = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('songs')
      .select('title, downloads')
      .order('downloads', { ascending: false })
      .limit(5);
    if (error) setError('Failed to load top songs: ' + error.message);
    else setTopSongs(data || []);
    setLoading(false);
  };

  const fetchTopPosts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('blog_posts')
      .select('title, views')
      .order('views', { ascending: false, nullsLast: true })
      .limit(5);
    if (error) console.warn('Top posts fetch warning: ' + error.message);
    else setTopPosts(data || []);
    setLoading(false);
  };

  const fetchSongOfTheWeek = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('song_of_the_week')
      .select('title, composer, audio_url')
      .single();
    if (error) console.warn('Song of the week fetch warning: ' + error.message);
    else {
      setSongOfTheWeekTitle(data?.title || '');
      setSongOfTheWeekComposer(data?.composer || '');
      setSongOfTheWeekHtml(data?.audio_url || '');
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleSongSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null); // Reset any existing error
    try {
      const songData = {
        title: songForm.title,
        composer: songForm.composer,
        google_drive_file_id: songForm.source === 'google_drive' ? songForm.google_drive_file_id : null,
        github_file_url: songForm.source === 'github' ? songForm.github_file_url : null,
        permalink: songForm.permalink || null,
        is_public: songForm.is_public,
        audio_url: songForm.audio_url || null,
        lyrics: songForm.lyrics || null,
        description: songForm.description || null,
        youtube_url: songForm.youtube_url || null,
        category: songForm.category || null,
        tags: songForm.tags ? songForm.tags.split(',').map(tag => tag.trim()) : null
      };

      // Check if permalink already exists (only for new songs)
      if (!editingSongId && songData.permalink) {
        const { data: existing, error: checkError } = await supabase
          .from('songs')
          .select('id')
          .eq('permalink', songData.permalink)
          .single();
        if (existing) {
          setError('A song with this permalink already exists. Please use a different permalink.');
          setLoading(false);
          return;
        }
      }

      if (editingSongId) {
        const { data: updatedSong, error } = await supabase
          .from('songs')
          .update(songData)
          .eq('id', editingSongId)
          .select()
          .single();
        if (error) throw error;
        setSongs(songs.map(song => (song.id === editingSongId ? updatedSong : song)));
        setEditingSongId(null);
        setSuccessMessage('Song updated successfully!');
      } else {
        const { data: newSong, error } = await supabase
          .from('songs')
          .insert([{ ...songData, downloads: 0 }])
          .select()
          .single();
        if (error) throw error;
        setSongs([...songs, newSong]);
        setSuccessMessage('Song added successfully!');
      }

      setSongForm(EMPTY_SONG_FORM);
      setQuillKey(q => q + 1);
    } catch (err) {
      setError('Failed to save song: ' + err.message);
    }
    setLoading(false);
  };

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const generatedPermalink = postForm.permalink.trim() || postForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      if (!postForm.title.trim()) {
        setError('Title is required');
        setLoading(false);
        return;
      }

      const tagsArray = postForm.tags
        ? postForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : null;

      const postData = {
        title: postForm.title,
        content: postForm.content,
        permalink: generatedPermalink,
        meta_description: postForm.meta_description || null,
        tags: tagsArray,
        category: postForm.category || null,
        focus_keyword: postForm.focus_keyword || null,
        featured_image_url: postForm.featured_image_url || null,
        views: editingPostId ? undefined : 0
      };

      if (editingPostId) {
        const { error } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', editingPostId);
        if (error) throw error;
        setEditingPostId(null);
        setSuccessMessage('Post updated successfully!');
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .insert([postData]);
        if (error) throw error;
        setSuccessMessage('Post added successfully!');
      }
      setPostForm({ title: '', content: '', permalink: '', meta_description: '', tags: '', category: '', focus_keyword: '', featured_image_url: '' });
      await fetchPosts();
    } catch (err) {
      setError('Failed to save post: ' + err.message);
    }
    setLoading(false);
  };

  const handleAdSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingAdId) {
        const { error } = await supabase
          .from('advertisements')
          .update({ 
            name: adForm.name, 
            code: adForm.code, 
            position: adForm.position, 
            is_active: adForm.is_active 
          })
          .eq('id', editingAdId);
        if (error) throw error;
        setEditingAdId(null);
        setSuccessMessage('Advertisement updated successfully!');
      } else {
        const { error } = await supabase
          .from('advertisements')
          .insert([{ 
            name: adForm.name, 
            code: adForm.code, 
            position: adForm.position, 
            is_active: adForm.is_active 
          }]);
        if (error) throw error;
        setSuccessMessage('Advertisement added successfully!');
      }
      setAdForm({ name: '', code: '', position: 'home_above_sotw', is_active: true });
      await fetchAds();
    } catch (err) {
      setError('Failed to save advertisement: ' + err.message);
    }
    setLoading(false);
  };

  const handleSongOfTheWeekSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cleanAudioUrl = (url) => {
        if (!url) return '';
        return url
          .replace(/<[^>]+>/g, '')
          .replace(/%3C[^%]+%3E/g, '')
          .trim();
      };

      const cleanedAudioUrl = cleanAudioUrl(songOfTheWeekHtml);

      const { data: existingData, error: fetchError } = await supabase
        .from('song_of_the_week')
        .select('id')
        .single();
      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

      const songData = {
        title: songOfTheWeekTitle,
        composer: songOfTheWeekComposer,
        audio_url: cleanedAudioUrl,
      };

      if (existingData) {
        const { error } = await supabase
          .from('song_of_the_week')
          .update(songData)
          .eq('id', existingData.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('song_of_the_week')
          .insert([songData]);
        if (error) throw error;
      }
      setSuccessMessage('Song of the Week updated successfully!');
    } catch (err) {
      setError('Failed to update Song of the Week: ' + err.message);
    }
    setLoading(false);
  };

  const editSong = async (song) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('songs')
        .select('*')
        .eq('id', song.id)
        .single();
      if (error) throw error;
      if (data) {
        const newSongForm = {
          title: data.title || '',
          composer: data.composer || '',
          google_drive_file_id: data.google_drive_file_id || '',
          github_file_url: data.github_file_url || '',
          permalink: data.permalink || '',
          is_public: data.is_public !== false,
          source: data.google_drive_file_id ? 'google_drive' : 'github',
          audio_url: data.audio_url || '',
          lyrics: data.lyrics || '',
          description: data.description || '',
          youtube_url: data.youtube_url || '',
          category: data.category || '',
          tags: data.tags ? data.tags.join(', ') : ''
        };
        setSongForm(newSongForm);
        setEditingSongId(data.id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError('No song data found for editing.');
      }
    } catch (err) {
      setError('Failed to load song for editing: ' + err.message);
    }
    setLoading(false);
  };

  const editPost = (post) => {
    setPostForm({ 
      title: post.title, 
      content: post.content, 
      permalink: post.permalink || '', 
      meta_description: post.meta_description || '', 
      tags: post.tags ? post.tags.join(', ') : '',
      category: post.category || '', 
      focus_keyword: post.focus_keyword || '',
      featured_image_url: post.featured_image_url || ''
    });
    setEditingPostId(post.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const editAd = (ad) => {
    setAdForm({ 
      name: ad.name, 
      code: ad.code, 
      position: ad.position, 
      is_active: ad.is_active 
    });
    setEditingAdId(ad.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteSong = async (id) => {
    if (window.confirm('Are you sure you want to delete this song?')) {
      setLoading(true);
      const { error } = await supabase.from('songs').delete().eq('id', id);
      if (error) setError('Failed to delete song: ' + error.message);
      else {
        setSongs(songs.filter(song => song.id !== id));
        setSuccessMessage('Song deleted successfully!');
      }
      setLoading(false);
    }
  };

  const deletePost = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      setLoading(true);
      const { error } = await supabase.from('blog_posts').delete().eq('id', id);
      if (error) setError('Failed to delete post: ' + error.message);
      else {
        setPosts(posts.filter(post => post.id !== id));
        setSuccessMessage('Post deleted successfully!');
      }
      setLoading(false);
    }
  };

  const deleteAd = async (id) => {
    if (window.confirm('Are you sure you want to delete this advertisement?')) {
      setLoading(true);
      const { error } = await supabase.from('advertisements').delete().eq('id', id);
      if (error) setError('Failed to delete ad: ' + error.message);
      else {
        setAds(ads.filter(ad => ad.id !== id));
        setSuccessMessage('Advertisement deleted successfully!');
      }
      setLoading(false);
    }
  };

  const deleteBook = async (id) => {
    if (window.confirm('Are you sure you want to delete this book?')) {
      setLoading(true);
      const { error } = await supabase.from('music_books').delete().eq('id', id);
      if (error) setError('Failed to delete book: ' + error.message);
      else {
        setMusicBooks(musicBooks.filter(book => book.id !== id));
        setSuccessMessage('Book deleted successfully!');
        setSelectedBooks(selectedBooks.filter(bid => bid !== id));
      }
      setLoading(false);
    }
  };

  const handleBulkDeleteBooks = async () => {
    if (selectedBooks.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedBooks.length} book(s)?`)) {
      setLoading(true);
      const { error } = await supabase.from('music_books').delete().in('id', selectedBooks);
      if (error) setError('Failed to delete books: ' + error.message);
      else {
        setMusicBooks(musicBooks.filter(book => !selectedBooks.includes(book.id)));
        setSelectedBooks([]);
        setSuccessMessage(`${selectedBooks.length} book(s) deleted successfully!`);
      }
      setLoading(false);
    }
  };

  const toggleSongPublic = async (id, isPublic) => {
    setLoading(true);
    const { error } = await supabase.from('songs').update({ is_public: !isPublic }).eq('id', id);
    if (error) setError('Failed to update song status: ' + error.message);
    else {
      setSongs(songs.map(song => song.id === id ? { ...song, is_public: !isPublic } : song));
      setSuccessMessage(`Song set to ${!isPublic ? 'public' : 'private'} successfully!`);
    }
    setLoading(false);
  };

  const toggleAdActive = async (id, isActive) => {
    setLoading(true);
    const { error } = await supabase.from('advertisements').update({ is_active: !isActive }).eq('id', id);
    if (error) setError('Failed to update ad status: ' + error.message);
    else {
      setAds(ads.map(ad => ad.id === id ? { ...ad, is_active: !isActive } : ad));
      setSuccessMessage(`Ad set to ${!isActive ? 'active' : 'inactive'} successfully!`);
    }
    setLoading(false);
  };

  const toggleUserAdmin = async (id, isAdmin) => {
    if (id === user.id) {
      setError('Cannot modify your own admin status.');
      return;
    }
    setLoading(true);
    try {
      if (isAdmin) {
        const { error } = await supabase.from('admins').delete().eq('user_id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('admins').insert([{ user_id: id }]);
        if (error) throw error;
      }
      await fetchUsers();
      setSuccessMessage(`User ${isAdmin ? 'removed from' : 'added to'} admins successfully!`);
    } catch (err) {
      setError('Failed to update admin status: ' + err.message);
    }
    setLoading(false);
  };

  const toggleUserDonated = async (id, hasDonated) => {
    setLoading(true);
    try {
      const response = await fetch('/.netlify/functions/toggle-donated', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: id, hasDonated }),
      });
      if (!response.ok) throw new Error('Failed to toggle donation status');
      await fetchUsers();
      setSuccessMessage(`User donation status updated successfully!`);
    } catch (err) {
      setError('Failed to update donation status: ' + err.message);
    }
    setLoading(false);
  };

  const deleteUser = async (id) => {
    if (id === user.id) {
      setError('Cannot delete your own account.');
      return;
    }
    if (window.confirm('Are you sure you want to delete this user?')) {
      setLoading(true);
      try {
        const response = await fetch('/.netlify/functions/delete-user', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: id }),
        });
        if (!response.ok) throw new Error('Failed to delete user');
        await fetchUsers();
        setSuccessMessage('User deleted successfully!');
      } catch (err) {
        setError('Failed to delete user: ' + err.message);
      }
      setLoading(false);
    }
  };

  const toggleMessageRead = async (id, isRead) => {
    setLoading(true);
    const { error } = await supabase
      .from('contact_messages')
      .update({ is_read: !isRead })
      .eq('id', id);
    if (error) setError('Failed to update message status: ' + error.message);
    else {
      setMessages(messages.map(msg => msg.id === id ? { ...msg, is_read: !isRead } : msg));
      setSuccessMessage(`Message marked as ${!isRead ? 'read' : 'unread'} successfully!`);
    }
    setLoading(false);
  };

  const deleteMessage = async (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      setLoading(true);
      const { error } = await supabase.from('contact_messages').delete().eq('id', id);
      if (error) setError('Failed to delete message: ' + err.message);
      else {
        setMessages(messages.filter(msg => msg.id !== id));
        setSuccessMessage('Message deleted successfully!');
      }
      setLoading(false);
    }
  };

  const handleBulkDeleteSongs = async () => {
    if (selectedSongs.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedSongs.length} song(s)?`)) {
      setLoading(true);
      const { error } = await supabase.from('songs').delete().in('id', selectedSongs);
      if (error) setError('Failed to delete songs: ' + error.message);
      else {
        setSongs(songs.filter(song => !selectedSongs.includes(song.id)));
        setSelectedSongs([]);
        setSuccessMessage(`${selectedSongs.length} song(s) deleted successfully!`);
      }
      setLoading(false);
    }
  };

  const handleBulkTogglePublic = async (makePublic) => {
    if (selectedSongs.length === 0) return;
    setLoading(true);
    const { error } = await supabase.from('songs').update({ is_public: makePublic }).in('id', selectedSongs);
    if (error) setError('Failed to update song statuses: ' + error.message);
    else {
      setSongs(songs.map(song => selectedSongs.includes(song.id) ? { ...song, is_public: makePublic } : song));
      setSelectedSongs([]);
      setSuccessMessage(`${selectedSongs.length} song(s) set to ${makePublic ? 'public' : 'private'} successfully!`);
    }
    setLoading(false);
  };

  const fetchMusicBooks = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('music_books').select('*');
      if (error) throw error;
      setMusicBooks(data || []);
    } catch (err) {
      setError('Failed to load music books: ' + err.message);
    }
    setLoading(false);
  };

  // Log song search to Supabase search_logs
  const logSongSearch = async (keyword) => {
    // If blank or unchanged, don't log
    if (!keyword || keyword.trim().length < 2) return;
    try {
      // Get user id if available
      const userObj = await supabase.auth.getUser();
      const userId = userObj.data?.user?.id || null;
      await supabase.from('search_logs').insert([
        { keyword, user_id: userId }
      ]);
    } catch (err) {
      // Optionally handle/log error
    }
  };

  // Log post search to Supabase search_logs
  const logPostSearch = async (keyword) => {
    if (!keyword || keyword.trim().length < 2) return;
    try {
      const userObj = await supabase.auth.getUser();
      const userId = userObj.data?.user?.id || null;
      await supabase.from('search_logs').insert([
        { keyword, user_id: userId }
      ]);
    } catch (err) {
      // Optionally handle/log error
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: { matchVisual: false }
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'link', 'image'
  ];

  const filteredSongs = songs.filter(song => {
    const title = song.title || '';
    const composer = song.composer || '';
    const tags = song.tags || [];
    const matchesSearch = 
      title.toLowerCase().includes(songSearch.toLowerCase()) || 
      composer.toLowerCase().includes(songSearch.toLowerCase()) ||
      (tags && tags.some(tag => (tag || '').toLowerCase().includes(songSearch.toLowerCase())));
    const matchesFilter = 
      songFilter === 'all' || 
      (songFilter === 'public' && song.is_public) || 
      (songFilter === 'private' && !song.is_public);
    const matchesCategory = 
      songCategoryFilter === 'all' || (song.category || '') === songCategoryFilter;
    return matchesSearch && matchesFilter && matchesCategory;
  });

    const filteredUsers = users.filter(user => {
      const matchesSearch =
        (user.full_name || '').toLowerCase().includes(userSearch.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(userSearch.toLowerCase());
      const matchesTier =
        !userTierFilter || user.subscription_tier === userTierFilter;
      return matchesSearch && matchesTier;
    });

  const filteredVisitors = visitorData.filter(visit => {
  const searchLower = visitorSearch.toLowerCase();
  const matchesSearch =
    visit.ip_address.toLowerCase().includes(searchLower) ||
    (visit.profile?.email || '').toLowerCase().includes(searchLower) ||
    visit.page_url.toLowerCase().includes(searchLower);

  const matchesStatus =
    visitorStatusFilter === 'all' ||
    (visitorStatusFilter === 'online' && visit.is_online) ||
    (visitorStatusFilter === 'offline' && !visit.is_online);

  const matchesType =
    visitorTypeFilter === 'all' ||
    (visitorTypeFilter === 'registered' && visit.is_registered) ||
    (visitorTypeFilter === 'guest' && !visit.is_registered && !visit.is_bot) ||
    (visitorTypeFilter === 'bot' && visit.is_bot);

  return matchesSearch && matchesStatus && matchesType;
});

  if (!user) return null;

  const totalDownloads = songs.reduce((sum, song) => sum + (song.downloads || 0), 0);
  const publicSongs = songs.filter(song => song.is_public).length;
  const privateSongs = songs.length - publicSongs;

  const toGMTPlus1 = (date) => {
    const utcDate = new Date(date);
    const gmtPlus1Offset = 1 * 60 * 60 * 1000;
    return new Date(utcDate.getTime() + gmtPlus1Offset);
  };

  return (
    <div className="admin-container">
      {loading && <div className="loading-overlay">Loading...</div>}
      <div className="admin-header">
        <h2 className="admin-title">Admin Dashboard</h2>
        <button onClick={handleLogout} className="logout-button">Logout</button>
      </div>
      <div className="tab-bar">
        <button className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
        <button className={`tab-button ${activeTab === 'songs' ? 'active' : ''}`} onClick={() => setActiveTab('songs')}>Songs</button>
        <button className={`tab-button ${activeTab === 'posts' ? 'active' : ''}`} onClick={() => setActiveTab('posts')}>Blog Posts</button>
        <button className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}>Analytics</button>
        <button className={`tab-button ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>Manage Users</button>
        <button className={`tab-button ${activeTab === 'songOfTheWeek' ? 'active' : ''}`} onClick={() => setActiveTab('songOfTheWeek')}>Song of the Week</button>
        <button className={`tab-button ${activeTab === 'advert' ? 'active' : ''}`} onClick={() => setActiveTab('advert')}>Advert</button>
        <button className={`tab-button ${activeTab === 'messages' ? 'active' : ''}`} onClick={() => setActiveTab('messages')}>Messages</button>
        <button className={`tab-button ${activeTab === 'musicbooks' ? 'active' : ''}`} onClick={() => setActiveTab('musicbooks')}>Music Books</button>
      </div>
      <div className="admin-content">
        {error && (
          <div className="error-alert">
            <p>{error}</p>
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}
        {successMessage && (
          <div className="success-alert">
            <p>{successMessage}</p>
            <button onClick={() => setSuccessMessage(null)}>Dismiss</button>
          </div>
        )}
        {activeTab === 'overview' && (
          <div className="overview-grid">
            <div className="overview-card">
              <h3>Total Songs</h3>
              <p>{songs.length}</p>
            </div>
            <div className="overview-card">
              <h3>Total Downloads</h3>
              <p>{totalDownloads}</p>
            </div>
            <div className="overview-card">
              <h3>Total Users</h3>
              <p>{users.length}</p>
            </div>
            <div className="overview-card">
              <h3>Total Blog Posts</h3>
              <p>{posts.length}</p>
            </div>
          </div>
        )}
        
        {activeTab === 'songs' && (
          <div className="w-full max-w-7xl mx-auto p-4">
            {/* ADD CATEGORY MODAL */}
            {showAddCategory && (
              <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-xs p-6 border border-gray-100">
                  <h4 className="text-xl font-semibold mb-2">Add New Category</h4>
                  <input
                    type="text"
                    value={newCategory}
                    onChange={e => setNewCategory(e.target.value)}
                    className="border rounded-xl px-4 py-2 mb-4 w-full focus:outline-none focus:border-green-500"
                    placeholder="Category name"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      className="bg-green-700 text-white px-4 py-2 rounded-xl font-semibold hover:bg-green-800 transition"
                      onClick={() => {
                        if (newCategory && !SONG_CATEGORIES.includes(newCategory)) {
                          addSongCategory(newCategory);
                          setNewCategory('');
                          setShowAddCategory(false);
                        }
                      }}
                    >Add</button>
                    <button
                      className="bg-gray-200 px-4 py-2 rounded-xl hover:bg-gray-300 transition"
                      onClick={() => { setShowAddCategory(false); setNewCategory(''); }}
                    >Cancel</button>
                  </div>
                </div>
              </div>
            )}
        
            {/* Song Add/Edit Form */}
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-100">
              <h3 className="text-2xl font-semibold mb-3">{editingSongId ? 'Edit Song' : 'Add New Song'}</h3>
              <form key={editingSongId || 'new'} onSubmit={handleSongSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Song Title, Composer, Source, File ID/URL */}
                <input 
                  type="text" 
                  placeholder="Song Title" 
                  value={songForm.title} 
                  onChange={(e) => setSongForm({ ...songForm, title: e.target.value })} 
                  className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  required 
                />
                <input 
                  type="text" 
                  placeholder="Composer" 
                  value={songForm.composer} 
                  onChange={(e) => setSongForm({ ...songForm, composer: e.target.value })} 
                  className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  required 
                />
                <div>
                  <label className="font-medium text-sm text-gray-700 mb-1" htmlFor="source">File Source</label>
                  <select
                    id="source"
                    value={songForm.source}
                    onChange={(e) => setSongForm({ ...songForm, source: e.target.value })}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500 w-full"
                  >
                    <option value="google_drive">Google Drive</option>
                    <option value="github">GitHub (/public/pdf)</option>
                  </select>
                </div>
                {songForm.source === 'google_drive' ? (
                  <input 
                    type="text" 
                    placeholder="Google Drive File ID" 
                    value={songForm.google_drive_file_id} 
                    onChange={(e) => {
                      const input = e.target.value;
                      const match = input.match(/[-\w]{25,}/); // Extract the file ID
                      const fileId = match ? match[0] : input;
                      setSongForm({ ...songForm, google_drive_file_id: fileId });
                    }}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                    required 
                  />
                ) : (
                  <input 
                    type="text" 
                    placeholder="GitHub Raw URL (e.g., https://raw.githubusercontent.com/.../song.pdf)" 
                    value={songForm.github_file_url} 
                    onChange={(e) => setSongForm({ ...songForm, github_file_url: e.target.value })} 
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                    required 
                  />
                )}
                <input 
                  type="text" 
                  placeholder="Permalink" 
                  value={songForm.permalink} 
                  onChange={(e) => {
                    let input = e.target.value;
                    input = input.replace(/\[.*?\]/g, '');
                    input = input.replace(/[^\w\s-]/g, '');
                    input = input.trim().toLowerCase().replace(/\s+/g, '-');
                    setSongForm({ ...songForm, permalink: input });
                  }}
                  className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                />
                <input 
                  type="text" 
                  placeholder="Audio URL (e.g., https://archive.org/download/...)" 
                  value={songForm.audio_url} 
                  onChange={(e) => setSongForm({ ...songForm, audio_url: e.target.value })} 
                  className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                />
                
                {/* Lyrics Block */}
                <div className="w-full mt-3 col-span-1 md:col-span-2">
                  <div className="font-semibold text-base text-center mb-2">
                    Lyrics (with formatting)
                  </div>
                  <ReactQuill
                    key={quillKey + '-lyrics'}
                    theme="snow"
                    modules={quillModules}
                    formats={quillFormats}
                    value={songForm.lyrics}
                    onChange={val => setSongForm(f => ({ ...f, lyrics: val }))}
                    className="bg-white border rounded-lg"
                    placeholder="Enter full lyrics here (verses, stanzas, formatting allowed)"
                    style={{ minHeight: 150, marginBottom: 8 }}
                  />
                </div>

                {/* Description Block */}
                <div className="w-full mt-3 col-span-1 md:col-span-2">
                  <div className="font-semibold text-base text-center mb-2">
                    Description (for SEO & song intro)
                  </div>
                  <ReactQuill
                    key={quillKey + '-desc'}
                    theme="snow"
                    modules={quillModules}
                    formats={quillFormats}
                    value={songForm.description}
                    onChange={val => setSongForm(f => ({ ...f, description: val }))}
                    className="bg-white border rounded-lg"
                    placeholder="Describe the song, background, etc. (shown above lyrics)"
                    style={{ minHeight: 150, marginBottom: 8 }}
                  />
                </div>

                {/* YouTube URL Block */}
                <div className="w-full mt-6 col-span-1 md:col-span-2">
                  <div className="font-semibold text-base text-center mb-2">
                    YouTube Video URL (optional)
                  </div>
                  <input
                    type="text"
                    className="border rounded-lg px-3 py-2 w-full"
                    placeholder="e.g. https://www.youtube.com/watch?v=xxxxxx"
                    value={songForm.youtube_url}
                    onChange={e => setSongForm(f => ({ ...f, youtube_url: e.target.value }))}
                  />
                </div>

                {/* Category with add button */}
                <div className="flex gap-2 items-center">
                  <label className="font-medium text-sm text-gray-700 mb-1" htmlFor="category">Category</label>
                  <select
                    id="category"
                    value={songForm.category}
                    onChange={(e) => setSongForm({ ...songForm, category: e.target.value })}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500 flex-1"
                  >
                    <option value="">Select Category</option>
                    {SONG_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="bg-blue-100 text-blue-800 px-3 py-2 rounded-xl ml-2 text-xs font-semibold hover:bg-blue-200 transition"
                    onClick={() => setShowAddCategory(true)}
                    title="Add new category"
                  >+ Add</button>
                </div>
                <input 
                  type="text" 
                  placeholder="Tags (comma-separated, e.g., piano, vocal)" 
                  value={songForm.tags} 
                  onChange={(e) => setSongForm({ ...songForm, tags: e.target.value })} 
                  className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                />
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={songForm.is_public} 
                    onChange={(e) => setSongForm({ ...songForm, is_public: e.target.checked })} 
                    className="w-5 h-5"
                  />
                  <span className="text-sm text-gray-800">Public</span>
                </div>
                {/* Buttons */}
                <div className="col-span-1 md:col-span-2 flex gap-2 mt-2">
                  <button type="submit" className="bg-green-700 text-white px-5 py-2 rounded-xl font-semibold hover:bg-green-800 transition">
                    {editingSongId ? 'Update Song' : 'Add Song'}
                  </button>
                  {editingSongId && (
                    <button 
                      type="button" 
                      className="bg-gray-200 px-4 py-2 rounded-xl hover:bg-gray-300 transition"
                      onClick={() => { 
                        setSongForm(EMPTY_SONG_FORM); 
                        setEditingSongId(null); 
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
            
            {/* Filter Bar */}
            <div className="flex flex-wrap gap-2 mb-6 items-center bg-white px-3 py-3 rounded-2xl border border-gray-100 shadow">
              <form
                onSubmit={async e => {
                  e.preventDefault();
                  await logSongSearch(songSearch);
                }}
                className="flex items-center gap-2 w-full"
              >
                <input 
                  type="text" 
                  placeholder="Search songs or tags..." 
                  value={songSearch} 
                  onChange={e => setSongSearch(e.target.value)} 
                  className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500 w-full md:w-64"
                />
                <button
                  type="submit"
                  className="bg-green-100 text-green-900 px-4 py-2 rounded-xl ml-2 text-sm font-semibold hover:bg-green-200 transition"
                >
                  Search
                </button>
              </form>
              
              <select 
                value={songFilter} 
                onChange={(e) => setSongFilter(e.target.value)} 
                className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
              >
                <option value="all">All</option>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
              <select 
                value={songCategoryFilter} 
                onChange={(e) => setSongCategoryFilter(e.target.value)} 
                className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
              >
                <option value="all">All Categories</option>
                {SONG_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <button 
                onClick={() => {
                  setSongSearch('');
                  setSongFilter('all');
                  setSongCategoryFilter('all');
                  setSelectedSongs([]);
                }} 
                className="bg-gray-200 px-4 py-2 rounded-xl hover:bg-gray-300 transition"
              >
                Clear Filters
              </button>
              <button
                type="button"
                className="bg-blue-100 text-blue-800 px-3 py-2 rounded-xl ml-2 text-xs font-semibold hover:bg-blue-200 transition"
                onClick={() => setShowAddCategory(true)}
                title="Add new category"
              >+ Add Category</button>
            </div>
        
            {/* Bulk Actions */}
            <div className="flex gap-2 mb-6">
              <button 
                onClick={handleBulkDeleteSongs} 
                className="bg-rose-100 text-rose-700 px-4 py-2 rounded-xl font-semibold hover:bg-rose-200 transition"
                disabled={selectedSongs.length === 0}
              >
                Delete Selected ({selectedSongs.length})
              </button>
              <button 
                onClick={() => handleBulkTogglePublic(true)} 
                className="bg-green-100 text-green-900 px-4 py-2 rounded-xl font-semibold hover:bg-green-200 transition"
                disabled={selectedSongs.length === 0}
              >
                Make Public
              </button>
              <button 
                onClick={() => handleBulkTogglePublic(false)} 
                className="bg-yellow-100 text-yellow-900 px-4 py-2 rounded-xl font-semibold hover:bg-yellow-200 transition"
                disabled={selectedSongs.length === 0}
              >
                Make Private
              </button>
            </div>
        
            {/* Table */}
            <div className="overflow-x-auto bg-white rounded-2xl border border-gray-100 shadow">
              <table className="min-w-full divide-y divide-gray-100">
                <thead>
                  <tr className="bg-green-50">
                    <th className="p-3"><input 
                      type="checkbox" 
                      onChange={(e) => setSelectedSongs(e.target.checked ? filteredSongs.map(s => s.id) : [])} 
                      checked={selectedSongs.length === filteredSongs.length && filteredSongs.length > 0}
                      className="w-5 h-5"
                    /></th>
                    <th className="p-3 text-left">Title</th>
                    <th className="p-3 text-left">Composer</th>
                    <th className="p-3 text-left">Category</th>
                    <th className="p-3 text-left">Tags</th>
                    <th className="p-3 text-left">File Source</th>
                    <th className="p-3 text-center">Downloads</th>
                    <th className="p-3 text-center">Public</th>
                    <th className="p-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSongs.map(song => (
                    <tr key={song.id} className={selectedSongs.includes(song.id) ? 'bg-green-50' : ''}>
                      <td className="p-3 text-center">
                        <input 
                          type="checkbox" 
                          checked={selectedSongs.includes(song.id)} 
                          onChange={(e) => {
                            setSelectedSongs(e.target.checked 
                              ? [...selectedSongs, song.id] 
                              : selectedSongs.filter(id => id !== song.id));
                          }} 
                          className="w-5 h-5"
                        />
                      </td>
                      <td className="p-3">{song.title}</td>
                      <td className="p-3">{song.composer}</td>
                      {/* Category: Toggleable Dropdown */}
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <select
                            value={song.category || ''}
                            onChange={e => updateSongCategory(song.id, e.target.value)}
                            className="border rounded-xl px-2 py-1 text-xs focus:outline-none focus:border-green-500"
                          >
                            <option value="">Uncategorized</option>
                            {SONG_CATEGORIES.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setShowAddCategory(true)}
                            className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center ml-1"
                            title="Add new category"
                          >+</button>
                        </div>
                      </td>
                      <td className="p-3">
                        {song.tags ? (Array.isArray(song.tags) ? song.tags.join(', ') : song.tags) : 'N/A'}
                      </td>
                      <td className="p-3">{song.google_drive_file_id ? 'Google Drive' : 'GitHub'}</td>
                      <td className="p-3 text-center">{song.downloads || 0}</td>
                      <td className="p-3 text-center">
                        <button 
                          onClick={() => toggleSongPublic(song.id, song.is_public)} 
                          className={`px-3 py-1 rounded-full font-semibold text-xs ${song.is_public ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-900'} transition`}
                        >
                          {song.is_public ? 'Yes' : 'No'}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex flex-col sm:flex-row gap-2 justify-center">
                          <button onClick={() => editSong(song)} className="bg-green-100 text-green-900 px-3 py-1 rounded-xl font-semibold hover:bg-green-200 transition text-xs">Edit</button>
                          <button onClick={() => deleteSong(song.id)} className="bg-rose-100 text-rose-700 px-3 py-1 rounded-xl font-semibold hover:bg-rose-200 transition text-xs">Delete</button>
                          <a
                            href={song.permalink ? `/song/${song.permalink}` : song.google_drive_file_id ? `https://drive.google.com/file/d/${song.google_drive_file_id}` : song.github_file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-blue-100 text-blue-800 px-3 py-1 rounded-xl font-semibold hover:bg-blue-200 transition text-xs"
                          >
                            Preview
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredSongs.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center text-gray-400 py-8">No songs found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'musicbooks' && (
          <div className="w-full max-w-5xl mx-auto p-4">
            {/* Add/Edit Form */}
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-100">
              <h3 className="text-2xl font-semibold mb-3">{editingBookId ? 'Edit Music Book' : 'Add New Music Book'}</h3>
              <form
                key={editingBookId || 'new'}
                onSubmit={async e => {
                  e.preventDefault();
                  setLoading(true);
                  try {
                    const formData = {
                      title: bookForm.title,
                      author: bookForm.author,
                      google_drive_file_id: bookForm.google_drive_file_id,
                      description: bookForm.description,
                      is_public: bookForm.is_public,
                      tags: bookForm.tags ? bookForm.tags.split(',').map(tag => tag.trim()) : null,
                    };
                    if (editingBookId) {
                      const { data, error } = await supabase
                        .from('music_books')
                        .update(formData)
                        .eq('id', editingBookId)
                        .select()
                        .single();
                      if (error) throw error;
                      setMusicBooks(musicBooks.map(b => (b.id === editingBookId ? data : b)));
                      setEditingBookId(null);
                      setSuccessMessage('Music book updated successfully!');
                    } else {
                      const { data, error } = await supabase
                        .from('music_books')
                        .insert([formData])
                        .select()
                        .single();
                      if (error) throw error;
                      setMusicBooks([...musicBooks, data]);
                      setSuccessMessage('Music book added successfully!');
                    }
                    setBookForm({
                      title: '',
                      author: '',
                      google_drive_file_id: '',
                      description: '',
                      is_public: true,
                      tags: '',
                    });
                  } catch (err) {
                    setError('Failed to save music book: ' + err.message);
                  }
                  setLoading(false);
                }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <input
                  type="text"
                  placeholder="Book Title"
                  value={bookForm.title}
                  onChange={e => setBookForm({ ...bookForm, title: e.target.value })}
                  className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  required
                />
                <input
                  type="text"
                  placeholder="Author"
                  value={bookForm.author}
                  onChange={e => setBookForm({ ...bookForm, author: e.target.value })}
                  className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  required
                />
                <input
                  type="text"
                  placeholder="Google Drive File ID"
                  value={bookForm.google_drive_file_id}
                  onChange={e => {
                    const input = e.target.value;
                    const match = input.match(/[-\w]{25,}/);
                    const fileId = match ? match[0] : input;
                    setBookForm({ ...bookForm, google_drive_file_id: fileId });
                  }}
                  className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  required
                />
                <input
                  type="text"
                  placeholder="Tags (comma-separated)"
                  value={bookForm.tags}
                  onChange={e => setBookForm({ ...bookForm, tags: e.target.value })}
                  className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                />
                <div className="col-span-1 md:col-span-2">
                  <label className="font-medium text-sm text-gray-700 mb-1">Description</label>
                  <div className="bg-gray-50 rounded-xl border mb-3">
                    <ReactQuill
                      value={bookForm.description}
                      onChange={content => setBookForm({ ...bookForm, description: content })}
                      className="h-[150px] min-h-[120px] rounded-xl"
                      placeholder="Describe the music book..."
                      style={{ background: "white", minHeight: 120, height: 150 }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={bookForm.is_public}
                    onChange={e => setBookForm({ ...bookForm, is_public: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-sm text-gray-800">Public</span>
                </div>
                <div className="col-span-1 md:col-span-2 flex gap-2 mt-2">
                  <button type="submit" className="bg-green-700 text-white px-5 py-2 rounded-xl font-semibold hover:bg-green-800 transition">
                    {editingBookId ? 'Update Book' : 'Add Book'}
                  </button>
                  {editingBookId && (
                    <button
                      type="button"
                      className="bg-gray-200 px-4 py-2 rounded-xl hover:bg-gray-300 transition"
                      onClick={() => {
                        setBookForm({
                          title: '',
                          author: '',
                          google_drive_file_id: '',
                          description: '',
                          is_public: true,
                          tags: '',
                        });
                        setEditingBookId(null);
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
        
            {/* Bulk Actions for Music Books */}
            <div className="flex gap-2 mb-4">
              <button 
                onClick={handleBulkDeleteBooks} 
                className="bg-rose-100 text-rose-700 px-4 py-2 rounded-xl font-semibold hover:bg-rose-200 transition"
                disabled={selectedBooks.length === 0}
              >
                Delete Selected ({selectedBooks.length})
              </button>
            </div>
            
            {/* Music Books Table */}
            <div className="overflow-x-auto bg-white rounded-2xl border border-gray-100 shadow">
              <table className="min-w-full divide-y divide-gray-100">
                <thead>
                  <tr className="bg-green-50">
                    <th className="p-3 text-left">
                      <input
                        type="checkbox"
                        onChange={e => setSelectedBooks(e.target.checked ? musicBooks.map(b => b.id) : [])}
                        checked={selectedBooks.length === musicBooks.length && musicBooks.length > 0}
                        className="w-5 h-5"
                      />
                    </th>
                    <th className="p-3 text-left">Title</th>
                    <th className="p-3 text-left">Author</th>
                    <th className="p-3 text-left">Tags</th>
                    <th className="p-3 text-center">Public</th>
                    <th className="p-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {musicBooks.map(book => (
                    <tr key={book.id} className={selectedBooks.includes(book.id) ? 'bg-green-50' : ''}>
                      <td className="p-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedBooks.includes(book.id)}
                          onChange={e => {
                            setSelectedBooks(e.target.checked
                              ? [...selectedBooks, book.id]
                              : selectedBooks.filter(id => id !== book.id));
                          }}
                          className="w-5 h-5"
                        />
                      </td>
                      <td className="p-3">{book.title}</td>
                      <td className="p-3">{book.author}</td>
                      <td className="p-3">{book.tags ? (Array.isArray(book.tags) ? book.tags.join(', ') : book.tags) : 'N/A'}</td>
                      <td className="p-3 text-center">
                        {book.is_public ? (
                          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-bold">Yes</span>
                        ) : (
                          <span className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full text-xs font-bold">No</span>
                        )}
                      </td>
                      <td className="p-3 text-center flex gap-2">
                        <button
                          onClick={() => {
                            setBookForm({
                              title: book.title,
                              author: book.author,
                              google_drive_file_id: book.google_drive_file_id,
                              description: book.description,
                              is_public: book.is_public,
                              tags: book.tags ? (Array.isArray(book.tags) ? book.tags.join(', ') : book.tags) : '',
                            });
                            setEditingBookId(book.id);
                          }}
                          className="bg-green-100 text-green-900 px-3 py-1 rounded-xl font-semibold hover:bg-green-200 transition text-xs"
                        >
                          Edit
                        </button>
                        <a
                          href={`https://drive.google.com/file/d/${book.google_drive_file_id}/view`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-blue-100 text-blue-800 px-3 py-1 rounded-xl font-semibold hover:bg-blue-200 transition text-xs"
                        >
                          View
                        </a>
                        <button
                          onClick={() => deleteBook(book.id)}
                          className="bg-rose-100 text-rose-700 px-3 py-1 rounded-xl font-semibold hover:bg-rose-200 transition text-xs"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {musicBooks.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-gray-400 py-8">No music books found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )} {/* <-- This closes the condition! */}
        
        {activeTab === 'posts' && (
          <div className="w-full max-w-5xl mx-auto p-4">
            {/* Alerts */}
            {error && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
                <span>{error}</span>
                <button onClick={() => setError(null)} className="text-rose-700 hover:underline ml-4 text-xs">Dismiss</button>
              </div>
            )}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
                <span>{successMessage}</span>
                <button onClick={() => setSuccessMessage(null)} className="text-green-700 hover:underline ml-4 text-xs">Dismiss</button>
              </div>
            )}
        
            {/* Filter/Search bar */}
            <div className="flex flex-col md:flex-row gap-2 mb-6 items-center">
              <form
                onSubmit={async e => {
                  e.preventDefault();
                  await logPostSearch(postSearch);
                }}
                className="flex items-center gap-2 w-full"
              >
                <input
                  type="text"
                  className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500 w-full md:w-80"
                  placeholder="Search posts by title or tag..."
                  value={postSearch || ""}
                  onChange={e => setPostSearch(e.target.value)}
                />
                <button
                  type="submit"
                  className="bg-green-100 text-green-900 px-4 py-2 rounded-xl ml-2 text-sm font-semibold hover:bg-green-200 transition"
                >
                  Search
                </button>
              </form>

              <button
                className="bg-green-100 text-green-900 px-3 py-2 rounded-xl shadow hover:bg-green-200 transition text-sm ml-auto"
                onClick={fetchPosts}
              >
                Refresh
              </button>
            </div>
        
            {/* Add/Edit Form Card */}
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-100">
              <h3 className="text-2xl font-semibold mb-3">{editingPostId ? 'Edit Blog Post' : 'Add New Blog Post'}</h3>
              <form onSubmit={handlePostSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-3">
                  <label className="font-medium text-sm text-gray-700" htmlFor="title">Title *</label>
                  <input
                    id="title"
                    type="text"
                    placeholder="Enter post title"
                    value={postForm.title}
                    onChange={e => setPostForm({ ...postForm, title: e.target.value })}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                    required
                  />
              
                  <label className="font-medium text-sm text-gray-700" htmlFor="permalink">Permalink</label>
                  <input
                    id="permalink"
                    type="text"
                    placeholder="e.g. beautiful-music-post"
                    value={postForm.permalink}
                    onChange={e => setPostForm({ ...postForm, permalink: e.target.value })}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  />
              
                  <label className="font-medium text-sm text-gray-700" htmlFor="category">Category</label>
                  <input
                    id="category"
                    type="text"
                    placeholder="e.g. Vocal Tips"
                    value={postForm.category}
                    onChange={e => setPostForm({ ...postForm, category: e.target.value })}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  />
              
                  <label className="font-medium text-sm text-gray-700" htmlFor="tags">Tags (comma-separated)</label>
                  <input
                    id="tags"
                    type="text"
                    placeholder="e.g. music, choir, vocal"
                    value={postForm.tags}
                    onChange={e => setPostForm({ ...postForm, tags: e.target.value })}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  />
                </div>
              
                <div className="flex flex-col gap-3">
                  <label className="font-medium text-sm text-gray-700" htmlFor="featured_image_url">Featured Image URL</label>
                  <input
                    id="featured_image_url"
                    type="text"
                    placeholder="https://..."
                    value={postForm.featured_image_url}
                    onChange={e => setPostForm({ ...postForm, featured_image_url: e.target.value })}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  />
              
                  {postForm.featured_image_url && (
                    <img src={postForm.featured_image_url} alt="Featured" className="rounded-xl border w-full h-32 object-cover" />
                  )}
              
                  <label className="font-medium text-sm text-gray-700" htmlFor="meta_description">Meta Description</label>
                  <input
                    id="meta_description"
                    type="text"
                    placeholder="Short SEO description"
                    value={postForm.meta_description}
                    onChange={e => setPostForm({ ...postForm, meta_description: e.target.value })}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  />
              
                  <label className="font-medium text-sm text-gray-700" htmlFor="focus_keyword">Focus Keyword</label>
                  <input
                    id="focus_keyword"
                    type="text"
                    placeholder="e.g. choir tips"
                    value={postForm.focus_keyword}
                    onChange={e => setPostForm({ ...postForm, focus_keyword: e.target.value })}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  />
                </div>
              
                {/* Full width content editor */}
                <div className="col-span-1 md:col-span-2 flex flex-col mb-4">
                  <label className="font-medium text-sm text-gray-700 mb-1" htmlFor="content">Content</label>
                  <div className="bg-gray-50 rounded-xl border mb-3">
                    <ReactQuill
                      value={postForm.content}
                      onChange={content => setPostForm({ ...postForm, content })}
                      modules={quillModules}
                      formats={quillFormats}
                      className="h-[350px] min-h-[300px] rounded-xl"
                      placeholder="Write your blog post content here..."
                      style={{ background: "white", minHeight: 300, height: 350 }}
                    />
                  </div>
                </div>
                {/* Buttons, always at bottom, full width */}
                <div className="col-span-1 md:col-span-2 flex gap-2 mb-2">
                  <button type="submit" className="bg-green-700 text-white px-5 py-2 rounded-xl font-semibold hover:bg-green-800 transition">
                    {editingPostId ? "Update Post" : "Add Post"}
                  </button>
                  <button type="button"
                    onClick={() => {
                      setPostForm({
                        title: '', content: '', permalink: '', meta_description: '', tags: '', category: '', focus_keyword: '', featured_image_url: ''
                      });
                      setEditingPostId(null);
                    }}
                    className="bg-gray-200 px-4 py-2 rounded-xl hover:bg-gray-300 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>

            </div>
        
            {/* Posts Grid w/ Pagination */}
            <div>
              <div className="grid md:grid-cols-2 gap-5">
                {filteredPosts.map(post => (
                  <div key={post.id} className="bg-white shadow rounded-2xl border border-gray-100 flex flex-col p-5 relative hover:shadow-lg transition">
                    {post.featured_image_url && (
                      <img src={post.featured_image_url} alt="" className="rounded-lg w-full h-36 object-cover mb-3" />
                    )}
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="font-bold text-lg text-green-700">{post.title}</span>
                      <span className="ml-auto flex gap-1">
                        <button onClick={() => editPost(post)} className="text-xs bg-green-100 text-green-900 rounded-lg px-3 py-1 hover:bg-green-200 font-semibold transition">Edit</button>
                        <button onClick={() => deletePost(post.id)} className="text-xs bg-rose-100 text-rose-700 rounded-lg px-3 py-1 hover:bg-rose-200 font-semibold transition">Delete</button>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {post.tags && post.tags.length > 0 && (
                        post.tags.map(tag => (
                          <span key={tag} className="text-xs bg-green-50 border border-green-100 text-green-800 rounded-full px-2 py-0.5 mr-1">{tag}</span>
                        ))
                      )}
                      {post.category && (
                        <span className="text-xs bg-green-200 text-green-900 rounded-full px-2 py-0.5">{post.category}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mb-1">
                      <span>Created: {new Date(post.created_at).toLocaleDateString()}</span>
                      {post.views != null && (
                        <span className="ml-3">Views: {post.views}</span>
                      )}
                    </div>
                    <div className="line-clamp-3 text-gray-600 text-sm mb-2" dangerouslySetInnerHTML={{ __html: post.content }} />
                    <a
                      href={`/blog/${post.permalink || post.id}`}
                      className="inline-block text-green-700 hover:underline text-xs font-semibold mt-auto"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Post
                    </a>
                  </div>
                ))}
                {filteredPosts.length === 0 && (
                  <div className="col-span-2 text-center text-gray-400 py-8">No posts found.</div>
                )}
              </div>
              {/* Pagination */}
              <div className="flex justify-center gap-4 mt-8">
                <button
                  onClick={() => setPostPage(prev => Math.max(0, prev - 1))}
                  disabled={postPage === 0}
                  className="px-4 py-2 bg-gray-100 rounded-xl text-gray-700 font-semibold hover:bg-gray-200 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="self-center">Page {postPage + 1}</span>
                <button
                  onClick={() => setPostPage(prev => (filteredPosts.length === postsPerPage ? prev + 1 : prev))}
                  disabled={filteredPosts.length < postsPerPage}
                  className="px-4 py-2 bg-gray-100 rounded-xl text-gray-700 font-semibold hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="w-full max-w-7xl mx-auto px-4 py-8">
            {/* Alerts */}
            {error && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
                <span>{error}</span>
                <button onClick={() => setError(null)} className="text-rose-700 hover:underline ml-4 text-xs">Dismiss</button>
              </div>
            )}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
                <span>{successMessage}</span>
                <button onClick={() => setSuccessMessage(null)} className="text-green-700 hover:underline ml-4 text-xs">Dismiss</button>
              </div>
            )}
        
            {/* Quick Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white shadow-lg rounded-2xl p-6 flex flex-col border border-gray-100 hover:shadow-xl transition">
                <div className="text-2xl font-bold text-green-700 mb-2">{visitorData.length}</div>
                <div className="text-sm text-gray-600 flex items-center gap-2">
                  <span className="i-lucide-users w-5 h-5"></span> Unique Visitors (Recent)
                </div>
              </div>
              <div className="bg-white shadow-lg rounded-2xl p-6 flex flex-col border border-gray-100 hover:shadow-xl transition">
                <div className="text-2xl font-bold text-green-700 mb-2">{users.length}</div>
                <div className="text-sm text-gray-600 flex items-center gap-2">
                  <span className="i-lucide-user-check w-5 h-5"></span> Total Users
                </div>
              </div>
              <div className="bg-white shadow-lg rounded-2xl p-6 flex flex-col border border-gray-100 hover:shadow-xl transition">
                <div className="text-2xl font-bold text-green-700 mb-2">{songs.length}</div>
                <div className="text-sm text-gray-600 flex items-center gap-2">
                  <span className="i-lucide-music w-5 h-5"></span> Total Songs
                </div>
              </div>
              <div className="bg-white shadow-lg rounded-2xl p-6 flex flex-col border border-gray-100 hover:shadow-xl transition">
                <div className="text-2xl font-bold text-green-700 mb-2">{posts.length}</div>
                <div className="text-sm text-gray-600 flex items-center gap-2">
                  <span className="i-lucide-notebook-pen w-5 h-5"></span> Blog Posts
                </div>
              </div>
            </div>
        
            {/* Filter Bar */}
            <div className="bg-white shadow-lg rounded-2xl p-4 mb-8 border border-gray-100">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <input
                  type="text"
                  placeholder="Search by IP, email, or page URL..."
                  value={visitorSearch}
                  onChange={(e) => setVisitorSearch(e.target.value)}
                  className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500 w-full sm:w-64"
                />
                <div className="flex gap-2 flex-wrap">
                  <select
                    value={visitorTimeFilter}
                    onChange={(e) => { setVisitorTimeFilter(e.target.value); setVisitorPage(0); fetchVisitors(); }}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  >
                    <option value="all">All Time</option>
                    <option value="24h">Last 24 Hours</option>
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                  </select>
                  <select
                    value={visitorStatusFilter}
                    onChange={(e) => setVisitorStatusFilter(e.target.value)}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  >
                    <option value="all">All Statuses</option>
                    <option value="online">Online</option>
                    <option value="offline">Offline</option>
                  </select>
                  <select
                    value={visitorTypeFilter}
                    onChange={(e) => setVisitorTypeFilter(e.target.value)}
                    className="border rounded-xl px-4 py-2 focus:outline-none focus:border-green-500"
                  >
                    <option value="all">All Types</option>
                    <option value="registered">Registered</option>
                    <option value="guest">Guest</option>
                    <option value="bot">Bot</option>
                  </select>
                  <button
                    onClick={() => { setVisitorSearch(''); setVisitorTimeFilter('all'); setVisitorStatusFilter('all'); setVisitorTypeFilter('all'); setVisitorPage(0); fetchVisitors(); }}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-300 transition"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
        
            {/* Visitor Table */}
            <div className="bg-white shadow-lg rounded-2xl p-6 mb-8 border border-gray-100">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <span className="i-lucide-users w-5 h-5"></span> Visitor Details
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">IP Address</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Page Visited</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Visit Time</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Tier</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredVisitors.slice(visitorPage * 10, (visitorPage + 1) * 10).map((visit, idx) => (
                      <tr key={idx} className="hover:bg-green-50 transition">
                        <td className="px-4 py-3 text-sm text-gray-700 font-mono">{visit.ip_address}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{visit.profiles?.email || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{visit.page_url}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {toGMTPlus1(visit.visit_timestamp).toLocaleString('en-US', { timeZone: 'Africa/Lagos' })}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {visit.profiles?.subscription_tier || 'None'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              visit.is_online ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {visit.is_online ? 'Online' : 'Offline'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              visit.is_bot
                                ? 'bg-red-100 text-red-700'
                                : visit.is_registered
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}
                          >
                            {visit.is_bot ? 'Bot' : visit.is_registered ? 'Registered' : 'Guest'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {filteredVisitors.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                          No visitors found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => setVisitorPage((prev) => Math.max(prev - 1, 0))}
                  disabled={visitorPage === 0}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-xl disabled:opacity-50 hover:bg-gray-300 transition"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {visitorPage + 1} of {Math.ceil(filteredVisitors.length / 10)}
                </span>
                <button
                  onClick={() => setVisitorPage((prev) => prev + 1)}
                  disabled={visitorPage >= Math.ceil(filteredVisitors.length / 10) - 1}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-xl disabled:opacity-50 hover:bg-gray-300 transition"
                >
                  Next
                </button>
              </div>
            </div>
        
            {/* Trends & Top Items */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              {/* Visitor Trend */}
              <div className="bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <span className="i-lucide-trending-up w-5 h-5"></span> Visitor Trend (Last 30 Days)
                </h3>
                <div className="w-full h-40 flex items-end gap-1">
                  {Array.from({ length: 30 }, (_, i) => (
                    <div
                      key={i}
                      className="bg-green-400 rounded-t"
                      style={{
                        width: '3%',
                        height: `${Math.max(
                          16,
                          Math.floor(
                            (100 *
                              visitorData.filter((v) =>
                                new Date(v.visit_timestamp).getDate() ===
                                new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).getDate()
                              ).length) /
                              (Math.max(
                                ...Array.from({ length: 30 }, (_, j) =>
                                  visitorData.filter((v) =>
                                    new Date(v.visit_timestamp).getDate() ===
                                    new Date(Date.now() - (29 - j) * 24 * 60 * 60 * 1000).getDate()
                                  ).length
                                )
                              ) || 1)
                          )
                        )}%`,
                      }}
                      title={`Day ${i + 1}`}
                    ></div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-gray-500">Each bar represents one day</div>
              </div>
              {/* Download Trend */}
              <div className="bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <span className="i-lucide-download w-5 h-5"></span> Downloads (Last 30 Days)
                </h3>
                <div className="w-full h-40 flex items-end gap-1">
                  {Array.from({ length: 30 }, (_, i) => (
                    <div
                      key={i}
                      className="bg-green-500 rounded-t"
                      style={{
                        width: '3%',
                        height: `${Math.max(
                          16,
                          Math.floor(
                            (100 *
                              songs.reduce(
                                (acc, song) =>
                                  acc +
                                  (song.downloads_by_day?.[
                                    new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
                                  ] || 0),
                                0
                              )) /
                              (Math.max(
                                ...Array.from({ length: 30 }, (_, j) =>
                                  songs.reduce(
                                    (acc, song) =>
                                      acc +
                                      (song.downloads_by_day?.[
                                        new Date(Date.now() - (29 - j) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
                                      ] || 0),
                                    0
                                  )
                                )
                              ) || 1)
                          )
                        )}%`,
                      }}
                      title={`Day ${i + 1}`}
                    ></div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-gray-500">Each bar represents one day</div>
              </div>
            </div>
        
            {/* Top Items & Search Trends */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {/* Top Songs */}
              <div className="bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
                <h3 className="font-semibold text-base mb-4 flex items-center gap-2">
                  <span className="i-lucide-music-2 w-5 h-5"></span> Top Songs (All Time)
                </h3>
                <ul className="text-sm">
                  {topSongs.length ? (
                    topSongs.map((song) => (
                      <li key={song.title} className="flex justify-between mb-2">
                        <span className="truncate">{song.title}</span>
                        <span className="text-green-700 font-semibold">{song.downloads}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-gray-400">No data</li>
                  )}
                </ul>
              </div>
              {/* Top Blog Posts */}
              <div className="bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
                <h3 className="font-semibold text-base mb-4 flex items-center gap-2">
                  <span className="i-lucide-notebook w-5 h-5"></span> Top Blog Posts
                </h3>
                <ul className="text-sm">
                  {topPosts.length ? (
                    topPosts.map((post) => (
                      <li key={post.title} className="flex justify-between mb-2">
                        <span className="truncate">{post.title}</span>
                        <span className="text-green-700 font-semibold">{post.views}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-gray-400">No data</li>
                  )}
                </ul>
              </div>
              {/* Search Trends */}
              <div className="bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
                <h3 className="font-semibold text-base mb-4 flex items-center gap-2">
                  <span className="i-lucide-search w-5 h-5"></span> Top Search Keywords
                </h3>
                <ul className="text-sm">
                  {searchTrends.length ? (
                    searchTrends.slice(0, 8).map((item) => (
                      <li key={item.keyword} className="flex justify-between mb-2">
                        <span className="truncate">{item.keyword}</span>
                        <span className="text-green-700 font-semibold">{item.count}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-gray-400">No data</li>
                  )}
                </ul>
              </div>
            </div>
        
            {/* Recent Activity Feed */}
            <div className="bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <span className="i-lucide-activity w-5 h-5"></span> Recent Activity
              </h3>
              <ul className="max-h-52 overflow-y-auto text-sm">
                {visitorData.slice(0, 12).map((visit, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-700 mb-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span className="font-mono text-xs text-gray-500">{visit.ip_address.slice(-5)}</span>
                    <span>
                      Visited <b className="text-green-700">{visit.page_url}</b>
                    </span>
                    <span className="ml-auto text-xs text-gray-500">
                      {toGMTPlus1(visit.visit_timestamp).toLocaleString('en-US', { timeZone: 'Africa/Lagos' })}
                    </span>
                  </li>
                ))}
                {visitorData.length === 0 && <li className="text-gray-400">No recent visits.</li>}
              </ul>
            </div>
          </div>
        )}
        
{activeTab === 'users' && (
          <div className="w-full max-w-[96vw] mx-auto py-2 px-2 sm:px-4">
            {/* User Stats Overview */}
            <div className="overflow-x-auto mb-4">
              <div className="flex flex-nowrap gap-4">
                <div className="flex-1 bg-gradient-to-r from-green-900 via-green-600 to-green-400 rounded-2xl shadow p-5 flex flex-col items-center min-w-[180px] text-white">
                  <span className="text-3xl font-bold">{users.length}</span>
                  <span className="text-green-100 text-xs mt-1">Total Users</span>
                </div>
                <div className="flex-1 bg-green-50 rounded-2xl shadow p-5 flex flex-col items-center min-w-[150px]">
                  <span className="text-2xl font-bold text-green-900">{users.filter(u => u.subscription_tier === 'choir_member').length}</span>
                  <span className="text-green-700 text-xs mt-1">Choir Members</span>
                </div>
                <div className="flex-1 bg-green-100 rounded-2xl shadow p-5 flex flex-col items-center min-w-[150px]">
                  <span className="text-2xl font-bold text-green-800">{users.filter(u => u.subscription_tier === 'choir_supporter').length}</span>
                  <span className="text-green-700 text-xs mt-1">Supporters</span>
                </div>
                <div className="flex-1 bg-green-200 rounded-2xl shadow p-5 flex flex-col items-center min-w-[150px]">
                  <span className="text-2xl font-bold text-green-800">{users.filter(u => u.subscription_tier === 'choir_expert').length}</span>
                  <span className="text-green-700 text-xs mt-1">Experts</span>
                </div>
              </div>
            </div>
        
            {/* Search, Tier Filter & Add User */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-center mb-4 sticky top-0 z-10 bg-white/80 py-2 rounded-2xl shadow-sm">
              <input
                type="text"
                className="w-full sm:w-[270px] border border-green-300 rounded-xl p-2 focus:outline-none focus:border-green-500 text-sm transition"
                placeholder="Search by name or email..."
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
              <select
                className="border rounded-xl p-2 text-sm border-green-300 focus:outline-none focus:border-green-500 transition"
                value={userTierFilter || ""}
                onChange={e => setUserTierFilter(e.target.value)}
              >
                <option value="">All Tiers</option>
                <option value="choir_member">Choir Member</option>
                <option value="choir_supporter">Supporter</option>
                <option value="choir_expert">Expert</option>
              </select>
              <button
                className="ml-auto bg-green-700 text-white px-4 py-2 rounded-xl shadow hover:bg-green-800 font-bold transition"
                onClick={() => setUserModal({ open: true, mode: 'add', user: null })}
              >
                + Add User
              </button>
              <button onClick={fetchUsers} className="bg-green-100 text-green-900 px-3 py-2 rounded-xl shadow hover:bg-green-200 transition">
                Refresh
              </button>
            </div>
        
            {/* Bulk Actions */}
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                className="bg-green-700 text-white px-3 py-1 rounded-lg shadow hover:bg-green-800 transition text-xs disabled:opacity-50"
                onClick={async () => {
                  if (selectedUsers.length && window.confirm(`Delete ${selectedUsers.length} user(s)?`)) {
                    for (const id of selectedUsers) await deleteUser(id);
                    setSelectedUsers([]);
                    await fetchUsers();
                  }
                }}
                disabled={selectedUsers?.length === 0}
              >
                Delete Selected
              </button>
              <button
                className="bg-green-200 text-green-900 px-3 py-1 rounded-lg shadow hover:bg-green-300 transition text-xs disabled:opacity-50"
                onClick={() => setSelectedUsers(filteredUsers.map(u => u.id))}
                disabled={filteredUsers.length === 0}
              >
                Select All
              </button>
              <button
                className="bg-white text-green-900 px-3 py-1 rounded-lg shadow border border-green-200 hover:bg-green-50 transition text-xs disabled:opacity-50"
                onClick={() => setSelectedUsers([])}
                disabled={selectedUsers?.length === 0}
              >
                Clear Selection
              </button>
              <span className="ml-2 text-xs text-green-800 flex items-center">{selectedUsers.length} selected</span>
            </div>
        
            {/* Users Table with dual scrollbars */}
            <div className="relative rounded-2xl shadow-md mb-6 border border-green-200 bg-white">
              {/* Top Scrollbar */}
              <div
                className="overflow-x-auto scrollbar-thin scrollbar-thumb-green-500 scrollbar-track-green-100"
                ref={topScrollRef}
                onScroll={() => syncScroll(topScrollRef, bottomScrollRef)}
              >
                <div className="min-w-[900px] h-px"></div>
              </div>
        
              {/* Table with Bottom Scrollbar */}
              <div
                className="overflow-x-auto scrollbar-thin scrollbar-thumb-green-500 scrollbar-track-green-100"
                ref={bottomScrollRef}
                onScroll={() => syncScroll(bottomScrollRef, topScrollRef)}
              >
                <table className="min-w-[900px] w-full text-sm text-left divide-y divide-green-100">
                  <thead>
                    <tr className="bg-green-700 text-white uppercase text-xs tracking-wider">
                      <th className="p-3 rounded-tl-2xl">
                        <input
                          type="checkbox"
                          className="accent-green-600 w-4 h-4"
                          onChange={e => setSelectedUsers(e.target.checked ? filteredUsers.map(u => u.id) : [])}
                          checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        />
                      </th>
                      <th className="p-3 text-left font-semibold">Full Name</th>
                      <th className="p-3 text-left font-semibold">Email</th>
                      <th className="p-3 text-left font-semibold">Phone</th>
                      <th className="p-3 text-center font-semibold">Downloads<br className="sm:hidden" /> <span className="hidden sm:inline">(This Month)</span></th>
                      <th className="p-3 text-center font-semibold">Total Downloads</th>
                      <th className="p-3 text-center font-semibold">Tier</th>
                      <th className="p-3 text-center font-semibold">Tier Expires</th>
                      <th className="p-3 text-center font-semibold">Date Joined</th>
                      <th className="p-3 rounded-tr-2xl text-center font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-10 text-gray-400">No users found.</td>
                      </tr>
                    ) : (
                      filteredUsers.map(user => (
                        <tr
                          key={user.id}
                          className="odd:bg-white even:bg-green-50 hover:bg-green-100 transition-all duration-200"
                        >
                          <td className="p-3">
                            <input
                              type="checkbox"
                              className="accent-green-600 w-4 h-4"
                              checked={selectedUsers.includes(user.id)}
                              onChange={e => {
                                setSelectedUsers(e.target.checked
                                  ? [...selectedUsers, user.id]
                                  : selectedUsers.filter(id => id !== user.id)
                                );
                              }}
                            />
                          </td>
                          <td className="p-3 font-medium text-green-900">{user.full_name}</td>
                          <td className="p-3 text-green-800">{user.email}</td>
                          <td className="p-3">{user.phone_number || <span className="text-gray-400">-</span>}</td>
                          <td className="p-3 text-center">{user.downloads_this_month ?? user.download_count_month ?? 0}</td>
                          <td className="p-3 text-center">{user.download_count_month ?? 0}</td>
                          <td className="p-3 text-center">
                            <TierDropdown user={user} setUsers={setUsers} />
                          </td>
                          <td className="p-3 text-center">{user.subscription_expires_at ? new Date(user.subscription_expires_at).toLocaleDateString() : '-'}</td>
                          <td className="p-3 text-center">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</td>
                          <td className="p-3 flex gap-2 justify-center items-center">
                            <button
                              onClick={() => setUserModal({ open: true, mode: 'edit', user })}
                              className="bg-green-700 hover:bg-green-800 text-white rounded-lg px-3 py-1 text-xs font-bold shadow transition"
                            >Edit</button>
                            <button
                              onClick={() => setConfirmDelete({ open: true, user })}
                              className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg px-3 py-1 text-xs font-bold shadow transition"
                            >Delete</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
        
              {/* Scroll Shadows */}
              <div className="absolute top-0 bottom-0 left-0 w-4 bg-gradient-to-r from-green-100/20 to-transparent pointer-events-none z-10"></div>
              <div className="absolute top-0 bottom-0 right-0 w-4 bg-gradient-to-l from-green-100/20 to-transparent pointer-events-none z-10"></div>
            </div>
        
            {/* User Add/Edit Modal */}
            {userModal?.open && (
              <UserModal
                open={userModal.open}
                mode={userModal.mode}
                user={userModal.user}
                onClose={() => setUserModal({ open: false, mode: null, user: null })}
                onSave={async (data) => {
                  await fetchUsers();
                  setUserModal({ open: false, mode: null, user: null });
                  setSuccessMessage(`User ${userModal.mode === 'add' ? 'added' : 'updated'} successfully`);
                }}
                currentUser={user}
              />
            )}
        
            {/* Confirm Delete Modal */}
            {confirmDelete?.open && (
              <ConfirmDeleteModal
                user={confirmDelete.user}
                onClose={() => setConfirmDelete({ open: false, user: null })}
                onConfirm={async () => {
                  await deleteUser(confirmDelete.user.id);
                  setConfirmDelete({ open: false, user: null });
                }}
              />
            )}
          </div>
        )}

        {activeTab === 'songOfTheWeek' && (
          <div className="admin-form-card">
            <h3 className="admin-form-title">Song of the Week</h3>
            <form onSubmit={handleSongOfTheWeekSubmit} className="admin-modern-form-grid">
              <div className="admin-form-group">
                <label htmlFor="songOfTheWeekTitle">Title</label>
                <input
                  id="songOfTheWeekTitle"
                  type="text"
                  placeholder="Enter song title"
                  value={songOfTheWeekTitle}
                  onChange={(e) => setSongOfTheWeekTitle(e.target.value)}
                  className="admin-form-input"
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="songOfTheWeekComposer">Composer</label>
                <input
                  id="songOfTheWeekComposer"
                  type="text"
                  placeholder="Enter composer name"
                  value={songOfTheWeekComposer}
                  onChange={(e) => setSongOfTheWeekComposer(e.target.value)}
                  className="admin-form-input"
                />
              </div>
              <div className="admin-form-group full-width">
                <label htmlFor="songOfTheWeekHtml">Embed Code (iframe or audio URL)</label>
                <ReactQuill
                  id="songOfTheWeekHtml"
                  value={songOfTheWeekHtml}
                  onChange={setSongOfTheWeekHtml}
                  modules={quillModules}
                  formats={quillFormats}
                  className="admin-quill-editor"
                  placeholder="Paste embed code or audio URL here..."
                  style={{ height: '200px' }}
                />
              </div>
              <div className="admin-form-group full-width">
                <label>Preview</label>
                <div 
                  className="sotw-preview"
                  dangerouslySetInnerHTML={{ __html: songOfTheWeekHtml.replace(/<[^>]+>/g, '').trim() ? `<audio controls src="${songOfTheWeekHtml.replace(/<[^>]+>/g, '').trim()}"></audio>` : songOfTheWeekHtml }} 
                />
              </div>
              <div className="admin-form-actions">
                <button type="submit" className="admin-form-submit">Update Song of the Week</button>
              </div>
            </form>
          </div>
        )}
        {activeTab === 'advert' && (
          <div className="admin-advert-container">
            <div className="admin-form-card">
              <h3 className="admin-form-title">{editingAdId ? 'Edit Advertisement' : 'Add New Advertisement'}</h3>
              <form onSubmit={handleAdSubmit} className="admin-modern-form-grid">
                <div className="admin-form-group">
                  <label htmlFor="adName">Name</label>
                  <input
                    id="adName"
                    type="text"
                    placeholder="e.g., Banner Ad 1"
                    value={adForm.name}
                    onChange={(e) => setAdForm({ ...adForm, name: e.target.value })}
                    className="admin-form-input"
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label htmlFor="adPosition">Position</label>
                  <select
                    id="adPosition"
                    value={adForm.position}
                    onChange={(e) => setAdForm({ ...adForm, position: e.target.value })}
                    className="admin-form-input"
                  >
                    <option value="home_above_sotw">Home - Above Song of the Week</option>
                    <option value="home_below_sotw">Home - Below Song of the Week</option>
                    <option value="song_above_content">Song Page - Above Content</option>
                    <option value="song_below_content">Song Page - Below Content</option>
                    <option value="post_above_content">Post Page - Above Content</option>
                    <option value="post_below_content">Post Page - Below Content</option>
                  </select>
                </div>
                <div className="admin-form-group full-width">
                  <label htmlFor="adCode">Ad Code</label>
                  <textarea
                    id="adCode"
                    placeholder="Paste your ad code here (e.g., Google AdSense script)"
                    value={adForm.code}
                    onChange={(e) => setAdForm({ ...adForm, code: e.target.value })}
                    className="admin-form-input"
                    rows="5"
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-checkbox">
                    <input
                      type="checkbox"
                      checked={adForm.is_active}
                      onChange={(e) => setAdForm({ ...adForm, is_active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
                <div className="admin-form-actions">
                  <button type="submit" className="admin-form-submit">{editingAdId ? 'Update Ad' : 'Add Ad'}</button>
                  <button 
                    type="button" 
                    className="admin-cancel-button" 
                    onClick={() => { 
                      setAdForm({ name: '', code: '', position: 'home_above_sotw', is_active: true }); 
                      setEditingAdId(null); 
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Position</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ads.map(ad => (
                    <tr key={ad.id}>
                      <td>{ad.name}</td>
                      <td>{ad.position}</td>
                      <td>
                        <button 
                          onClick={() => toggleAdActive(ad.id, ad.is_active)} 
                          className={`admin-toggle-button ${ad.is_active ? 'active' : ''}`}
                        >
                          {ad.is_active ? 'Yes' : 'No'}
                        </button>
                      </td>
                      <td>
                        <button onClick={() => editAd(ad)} className="admin-edit-button">Edit</button>
                        <button onClick={() => deleteAd(ad.id)} className="admin-delete-button">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeTab === 'messages' && (
          <div className="admin-messages-container">
            <h3 className="analytics-section-title">Contact Messages</h3>
            {messages.length > 0 ? (
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Message</th>
                      <th>Received At (GMT+1)</th>
                      <th>Read</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((msg) => (
                      <tr
                        key={msg.id}
                        onClick={() => setSelectedMessage(msg)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{msg.name}</td>
                        <td>{msg.email}</td>
                        <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.message}</td>
                        <td>{toGMTPlus1(msg.created_at).toLocaleString()}</td>
                        <td>
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleMessageRead(msg.id, msg.is_read); }}
                            className={`admin-toggle-button ${msg.is_read ? 'active' : ''}`}
                          >
                            {msg.is_read ? 'Yes' : 'No'}
                          </button>
                        </td>
                        <td>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteMessage(msg.id); }}
                            className="admin-delete-button"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No messages available.</p>
            )}
            {selectedMessage && (
              <div
                style={{
                  position: 'fixed',
                  top: '0',
                  left: '0',
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    width: '500px',
                    maxHeight: '80%',
                    overflowY: 'auto',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  <h3>Message Details</h3>
                  <p><strong>Name:</strong> {selectedMessage.name}</p>
                  <p><strong>Email:</strong> {selectedMessage.email}</p>
                  <p><strong>Message:</strong></p>
                  <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{selectedMessage.message}</p>
                  <p><strong>Received At (GMT+1):</strong> {toGMTPlus1(selectedMessage.created_at).toLocaleString()}</p>
                  <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => toggleMessageRead(selectedMessage.id, selectedMessage.is_read)}
                      className={`admin-toggle-button ${selectedMessage.is_read ? 'active' : ''}`}
                    >
                      Mark as {selectedMessage.is_read ? 'Unread' : 'Read'}
                    </button>
                    <button
                      onClick={() => deleteMessage(selectedMessage.id)}
                      className="admin-delete-button"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setSelectedMessage(null)}
                      className="admin-cancel-button"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
